import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { authApi } from '../lib/api';
import { Mail, Loader2, CheckCircle, Zap, X } from 'lucide-react';

const DEV_LOGIN_VISIBLE = (process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEV_LOGIN === 'true');

export default function HVLogin({ overlay = false }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devToken, setDevToken] = useState(null);
  const [quickLoading, setQuickLoading] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  useEffect(() => {
    if (searchParams.get('demo') === 'hv') {
      setEmail('demo@hausverwaltung.de');
    }
    // Gewünschter Plan aus den Pricing-Buttons — wird nach dem Login im Onboarding übernommen
    const plan = searchParams.get('plan');
    if (plan) { try { localStorage.setItem('immo_plan_intent', plan); } catch {} }
  }, [searchParams]);

  const handleClose = () => navigate(-1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !privacyAccepted) return;
    setLoading(true);
    try {
      const response = await authApi.requestHVMagicLink(email, privacyAccepted);
      setSent(true);
      setDevToken(response.data.dev_token);
      toast.success('Login-Link wurde gesendet!');
    } catch (err) {
      toast.error(err.response?.status === 429
        ? 'Zu viele Versuche. Bitte in ein paar Minuten erneut probieren.'
        : (err.response?.data?.detail || 'Fehler beim Senden des Login-Links'));
    } finally {
      setLoading(false);
    }
  };

  const handleQuickLogin = async () => {
    setQuickLoading(true);
    try {
      const res = await authApi.devQuickLogin('hv');
      localStorage.setItem('hv_auth_token', res.data.token);
      localStorage.setItem('user_data', JSON.stringify(res.data.user));
      toast.success('Erfolgreich eingeloggt!');
      navigate('/app');
    } catch (err) {
      toast.error('Quick-Login fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
    } finally {
      setQuickLoading(false);
    }
  };

  const handleCopyToken = () => {
    if (devToken) {
      navigator.clipboard.writeText(`${window.location.origin}/app/auth/verify?token=${devToken}`);
      toast.success('Link kopiert!');
    }
  };

  const card = (
    <div className="w-full max-w-md">
      {/* Logo */}
      <Link to="/" className="flex items-center justify-center mb-6 sm:mb-8">
        <img src="/logo.png" alt="Immoverita" className="h-16 sm:h-24 w-auto" />
      </Link>

      <Card className="card-shadow">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Anmelden</CardTitle>
          <CardDescription>
            {sent ? 'Prüfen Sie Ihre E-Mails' : 'Melden Sie sich mit Ihrer E-Mail-Adresse an'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!sent ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">E-Mail-Adresse</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ihre@email.de"
                    className="pl-10"
                    required
                    autoFocus
                    data-testid="hv-login-email-input"
                  />
                </div>
              </div>
              <label className="flex items-start gap-2.5 text-xs text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  required
                  className="mt-0.5 accent-primary"
                  data-testid="hv-login-privacy-checkbox"
                />
                <span>
                  Ich habe die <Link to="/datenschutz" target="_blank" className="underline hover:text-foreground">Datenschutzerklärung</Link> gelesen.
                  Für neue Konten wird ein Testzugang angelegt; ohne Bestätigung des Login-Links wird er nach 30 Tagen gelöscht.
                </span>
              </label>
              <Button
                type="submit"
                disabled={loading || !email || !privacyAccepted}
                className="w-full"
                data-testid="hv-login-submit-button"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Magic Link senden
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  Wir haben einen Login-Link an <strong>{email}</strong> gesendet.
                </p>
              </div>

              {DEV_LOGIN_VISIBLE && devToken && (
                <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200 space-y-2">
                  <p className="text-xs font-medium text-yellow-800">
                    Testphase: Der Mailversand ist noch nicht aktiv — Sie können sich direkt einloggen.
                  </p>
                  <Button
                    onClick={() => navigate(`/app/auth/verify?token=${devToken}`)}
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-yellow-900"
                    data-testid="hv-magic-link-login-button"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Mit Magic Link einloggen
                  </Button>
                  <button onClick={handleCopyToken} className="text-[11px] text-yellow-800 underline w-full text-center">
                    Link kopieren
                  </button>
                </div>
              )}

              <Button
                variant="ghost"
                onClick={() => { setSent(false); setDevToken(null); }}
                className="w-full"
              >
                Andere E-Mail verwenden
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {DEV_LOGIN_VISIBLE && (
        <Card className="border-yellow-300 bg-yellow-50 mt-4">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-yellow-600" />
              <span className="text-sm font-medium text-yellow-800">Quick Demo Login</span>
            </div>
            <Button
              onClick={handleQuickLogin}
              disabled={quickLoading}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-yellow-900"
              data-testid="hv-quick-login-button"
            >
              {quickLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
              Als demo@hausverwaltung.de einloggen
            </Button>
          </CardContent>
        </Card>
      )}

      <p className="text-center text-sm text-muted-foreground mt-6">
        <Link to="/admin/login" className="hover:text-foreground">Admin Login</Link>
      </p>

      {/* Footer */}
      <footer className="mt-8 pt-6 border-t border-border/50 text-center">
        <nav className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground mb-3">
          <Link to="/datenschutz" className="hover:text-foreground transition-colors">Datenschutz</Link>
          <Link to="/impressum" className="hover:text-foreground transition-colors">Impressum</Link>
          <Link to="/agb" className="hover:text-foreground transition-colors">AGB</Link>
          <Link to="/ki-transparenz" className="hover:text-foreground transition-colors">KI-Transparenz</Link>
        </nav>
        <p className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} Immoverita. Alle Rechte vorbehalten.
        </p>
      </footer>
    </div>
  );

  // Overlay mode: rendered on top of blurred landing page
  if (overlay) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-background/50 backdrop-blur-md"
          onClick={handleClose}
        />
        {/* Close button */}
        <button
          onClick={handleClose}
          className="fixed top-4 right-4 z-20 p-2 rounded-full bg-background/80 hover:bg-background border border-border text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        {/* Card — scrollable, centered */}
        <div className="relative z-10 flex justify-center min-h-full py-12 px-4">
          {card}
        </div>
      </div>
    );
  }

  // Standalone mode: direct navigation (e.g. from email link)
  return (
    <div className="min-h-screen bg-muted/30 overflow-y-auto">
      <div className="flex justify-center py-12 px-4">
        {card}
      </div>
    </div>
  );
}
